import { useQuery, useMutation, useQueryClient } from 'react-query';

import { get, post } from '../utils/api';
import { useIntegrationMetadata } from '../utils/integrations';

async function getIntegrations() {
  const response = await get('/user/integrations');
  return response.data;
}

async function saveSettings(slug, settings) {
  const response = await post(`/user/integrations/${slug}/settings`, settings);
  return response.data;
}

async function connect(slug) {
  const response = await post(`/user/integrations/${slug}/connect`);

  return response.data;
}

async function disconnect(slug) {
  const response = await post(`/user/integrations/${slug}/disconnect`);

  return response.data;
}

export async function runAction(slug, action, params) {
  const response = await post(
    `/user/integrations/${slug}/actions/${action}`,
    params
  );

  return response.data;
}

export function useIntegrations() {
  const integrationMetadata = useIntegrationMetadata();

  const { data, ...rest } = useQuery('integrations', getIntegrations);

  const integrations = (data?.integrations || [])
    .filter(({ slug }) => !!integrationMetadata[slug])
    .map((integration) => ({
      ...integration,
      ...integrationMetadata[integration.slug],
    }));

  return {
    integrations,
    ...rest,
  };
}

export function useIntegrationActions() {
  const queryClient = useQueryClient();

  const {
    mutate: _connect,
    mutateAsync: _connectAsync,
    isSuccess: isConnectSuccess,
    isError: isConnectError,
  } = useMutation((slug) => connect(slug), {
    onSettled: () => {
      queryClient.invalidateQueries('integrations');
    },
  });

  const {
    mutate: _disconnect,
    mutateAsync: _disconnectAsync,
    isSuccess: isDisconnectSuccess,
    isError: isDisconnectError,
  } = useMutation((slug) => disconnect(slug), {
    onSettled: () => {
      queryClient.invalidateQueries('integrations');
    },
  });

  const {
    mutate: _saveSettings,
    mutateAsync: _saveSettingsAsync,
    isSuccess: isSaveSettingsSuccess,
    isError: isSaveSettingsError,
  } = useMutation(({ slug, settings }) => saveSettings(slug, settings), {
    onSettled: () => {
      queryClient.invalidateQueries('integrations');
    },
  });

  return {
    connect: _connect,
    connectAsync: _connectAsync,
    isConnectSuccess,
    isConnectError,
    disconnect: _disconnect,
    disconnectAsync: _disconnectAsync,
    isDisconnectSuccess,
    isDisconnectError,
    saveSettings: _saveSettings,
    saveSettingsAsync: _saveSettingsAsync,
    isSaveSettingsSuccess,
    isSaveSettingsError,
  };
}
